/**
 * ScrollObserverModule
 *
 * Implements the Intersection Observer API using the modern ES Module pattern.
 * It uses data attributes on elements with the class '.js-observer-trigger'
 * to define specific actions (animations, lazy loading, media controls)
 * to be executed when the element enters or leaves the viewport.
 */
const ScrollObserverModule = (() => {

    // --- Private Variables ---
    const TRIGGER_SELECTOR = '[data-js-observer-trigger]';

    // Registry to hold custom functions accessible via data-fn attribute
    // Stores objects: { callback: function, runOnce: boolean }
    const _triggerRegistry = {};

    /**
     * Executes the specific action defined by the element's data-action attribute.
     *
     * @private
     * @param {HTMLElement} element The observed element.
     * @param {boolean} isIntersecting True if the element is currently visible.
     * @param {IntersectionObserver} observerInstance The observer instance (used for unobserve).
     */
    function _handleElementAction(element, isIntersecting, observerInstance) {
        const action = element.dataset.action;

        // --- BUILT-IN ACTIONS ---

        // 1. Toggle Class (e.g., for CSS animations)
        if (action === 'toggleClass') {
            const className = element.dataset.targetClass
            if (className && isIntersecting) {
                element.classList.add(className)
                observerInstance.unobserve(element)
            }
        }

        // 2. Toggle Attribute (e.g., for video autoplay/pause)
        else if (action === 'toggleAttribute') {
            const attribute = element.dataset.targetAttribute;
            if (attribute) {
                if (isIntersecting) {
                    element.setAttribute(attribute, '');
                    if (element.tagName === 'VIDEO' || element.tagName === 'AUDIO') {
                        element.play().catch(e => console.error("Media Autoplay Blocked:", e));
                    }
                } else {
                    element.removeAttribute(attribute);
                    if (element.tagName === 'VIDEO' || element.tagName === 'AUDIO') {
                        element.pause();
                    }
                }
            }
        }

        // 3. Lazy Loading (Run once when entering the viewport)
        else if (action === 'lazyLoad' && isIntersecting) {
            const src = element.dataset.src;
            if (src && element.tagName === 'IMG') {
                element.src = src;
                // Stop observing after the image is loaded
                observerInstance.unobserve(element);
            }
        }

        // --- CUSTOM ACTION REGISTRY ---

        // 4. Custom Function Execution
        else if (_triggerRegistry[action]) {
            const triggerDefinition = _triggerRegistry[action];

            // Execute the custom function, passing the element and intersection state
            triggerDefinition.callback(element, isIntersecting);

            // Check if it's set to run once AND is currently intersecting
            if (triggerDefinition.runOnce && isIntersecting) {
                observerInstance.unobserve(element);
            }
        }
    }

    /**
     * The Intersection Observer callback function.
     *
     * @private
     * @param {IntersectionObserverEntry[]} entries
     * @param {IntersectionObserver} observer
     */
    function _intersectionCallback(entries, observer) {
        entries.forEach(entry => {
            _handleElementAction(entry.target, entry.isIntersecting, observer);
        });
    }

    /**
     * Looks for all elements with the trigger selector and sets up the observer(s).
     *
     * @private
     */
    function _observeElements() {
        const elementsToObserve = document.querySelectorAll(TRIGGER_SELECTOR);
        if (elementsToObserve.length === 0) {
            console.log('ScrollObserverModule: No elements found to observe.');
            return;
        }

        const defaultOptions = {
            root: null,
            rootMargin: '0px 0px -100px 0px',
            threshold: 0.01
        };

        const defaultObserver = new IntersectionObserver(_intersectionCallback, defaultOptions);

        elementsToObserve.forEach(element => {
            const marginOverride = element.dataset.rootMargin;

            if (marginOverride) {
                // Need a separate observer for different rootMargins
                const individualOptions = {
                    ...defaultOptions,
                    rootMargin: marginOverride
                };
                const individualObserver = new IntersectionObserver(_intersectionCallback, individualOptions);
                individualObserver.observe(element);
            }
            else {
                defaultObserver.observe(element);
            }
        });
    }

    /**
     * Initializes the observer module.
     *
     * @public
     */
    function init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', _observeElements);
        }
        else {
            _observeElements();
        }
        console.log('ScrollObserverModule: Initialized and observing scroll triggers.');
    }

    // --- NEW Public Method for Registration ---

    /**
     * Allows external scripts to register a custom function to be executed
     * when an element with data-action="custom" and matching data-fn is observed.
     *
     * @public
     * @param {string} name The name used in the HTML's data-fn attribute.
     * @param {function(HTMLElement, boolean): void} callback The function to execute.
     * @param {boolean} [runOnce=true] If true, the observer stops after the first time it enters the viewport.
     */
    function registerTrigger(name, callback, runOnce = true) {
        if (typeof callback === 'function') {
            // Store the callback and the runOnce setting as an object
            _triggerRegistry[name] = {
                callback: callback,
                runOnce: runOnce
            };
            console.log(`ScrollObserverModule: Registered custom trigger "${name}" (Run Once: ${runOnce}).`);
        }
        else {
            console.error(`ScrollObserverModule: Cannot register "${name}". Callback must be a function.`);
        }
    }

    // --- Public Interface ---
    return {
        init: init,
        registerTrigger: registerTrigger // <-- REVEALED
    };
})();

/**
 * ToggleModule
 *
 * Implements a declarative system for toggling classes on target elements.
 * It uses a data-attribute system for linking toggles to targets and
 * includes specialized logic for managing "select all" group toggles.
 *
 * Requirements:
 * - Toggle element: MUST have [data-toggle="name"]
 * - Target element: MUST have [data-target="name"]
 * - Defocus behavior: Add [data-defocus] to toggle or target elements to close on outside click.
 */
const ToggleModule = (() => {
    // --- Private Variables ---
    const TOGGLE_SELECTOR = '[data-toggle]';
    const ACTIVE_CLASS = 'active';
    const EXPANDED_CLASS = 'is-expanded';
    const DEFAULT_ACTIVE_CLASS = 'default-is-active';

    // --- Private Helper Functions ---

    /**
     * Helper to collapse all currently active toggles and targets, 
     * excluding the ones that were just clicked.
     * @param {string} currentToggleName - The name of the newly clicked toggle.
     */
    function _collapseOthers(currentToggleName) {
        // 1. Find and deactivate all other active targets
        document.querySelectorAll(`[data-target].${ACTIVE_CLASS}`).forEach(targetEl => {
            if (targetEl.dataset.target !== currentToggleName) {
                targetEl.classList.remove(ACTIVE_CLASS);
                targetEl.classList.remove(EXPANDED_CLASS); // Also clean up expanded state if applicable
            }
        });

        // 2. Find and deactivate all other active toggles
        document.querySelectorAll(`${TOGGLE_SELECTOR}.${ACTIVE_CLASS}`).forEach(toggleEl => {
            if (toggleEl.dataset.toggle !== currentToggleName) {
                toggleEl.classList.remove(ACTIVE_CLASS);
                toggleEl.classList.remove(DEFAULT_ACTIVE_CLASS);
            }
        });
    }

    /**
     * Deactivates a specific toggle/target pair or group by toggle name.
     * @param {string} toggleName 
     */
    function _untoggleByName(toggleName) {
        // Deactivate targets matching the toggleName
        document.querySelectorAll(`[data-target="${toggleName}"].${ACTIVE_CLASS}`).forEach(targetEl => {
            targetEl.classList.remove(ACTIVE_CLASS, EXPANDED_CLASS, DEFAULT_ACTIVE_CLASS);
        });

        // Deactivate toggles matching the toggleName
        document.querySelectorAll(`[data-toggle="${toggleName}"].${ACTIVE_CLASS}`).forEach(toggleEl => {
            toggleEl.classList.remove(ACTIVE_CLASS, DEFAULT_ACTIVE_CLASS);
        });
    }

    /**
     * Toggles the 'is-active' and 'is-expanded' state for a single pair.
     * @param {HTMLElement} toggle - The clicked toggle element.
     * @param {HTMLElement} target - The element being controlled.
     */
    function _handleVisibility(toggle, target) {
        // Toggling the 'default-is-active' state off if present
        if (toggle.classList.contains(DEFAULT_ACTIVE_CLASS)) {
            toggle.classList.remove(DEFAULT_ACTIVE_CLASS);
        }
        if (target.classList.contains(DEFAULT_ACTIVE_CLASS)) {
            target.classList.remove(DEFAULT_ACTIVE_CLASS);
        }

        // Toggle the active classes
        toggle.classList.toggle(ACTIVE_CLASS);
        target.classList.toggle(ACTIVE_CLASS);

        // Optionally toggle 'is-expanded'
        if (target.classList.contains(EXPANDED_CLASS)) {
            target.classList.remove(EXPANDED_CLASS);
        }
    }

    /**
     * Handles the complex logic for toggles ending in '_all'.
     * @param {HTMLElement} allToggle - The 'all' toggle element that was clicked.
     * @param {string} toggleName - The full name (e.g., 'category_all').
     */
    function _handleToggleAll(allToggle, toggleName) {
        const prefix = toggleName.split('_')[0];

        // Determine the NEW state: it's the opposite of the button's current state.
        const isActivating = !allToggle.classList.contains(ACTIVE_CLASS);

        // 1. Toggle related target elements
        document.querySelectorAll('[data-target]').forEach(targetEl => {
            // Check if target is part of the group (assuming data-target value starts with the prefix)
            if (targetEl.dataset.target.startsWith(prefix)) {
                targetEl.classList.remove(DEFAULT_ACTIVE_CLASS);
                targetEl.classList.toggle(ACTIVE_CLASS, isActivating);
            }
        });

        // 2. Toggle related controller elements (excluding the 'all' toggle itself)
        document.querySelectorAll(TOGGLE_SELECTOR).forEach(toggleEl => {
            const currentToggleName = toggleEl.dataset.toggle;

            if (currentToggleName.startsWith(prefix) && currentToggleName !== toggleName) {
                toggleEl.classList.remove(DEFAULT_ACTIVE_CLASS);
                toggleEl.classList.toggle(ACTIVE_CLASS, isActivating);
            }
        });

        // 3. Toggle the 'all' controller itself
        allToggle.classList.toggle(ACTIVE_CLASS, isActivating);
    }

    /**
     * Handles document-wide clicks to close elements with `data-defocus` attribute.
     * @param {MouseEvent} evt 
     */
    function _handleOutsideClick(evt) {
        const activeDefocusElements = document.querySelectorAll(`.${ACTIVE_CLASS}[data-defocus]`);

        activeDefocusElements.forEach(element => {
            const toggleName = element.dataset.toggle || element.dataset.target;
            if (!toggleName) return;

            // Find all associated DOM elements (toggle and target) for this name
            const toggleEl = document.querySelector(`[data-toggle="${toggleName}"]`);
            const targetEl = document.querySelector(`[data-target="${toggleName}"]`);

            // Check if the click target is inside either the toggle button or the target content
            const clickedInsideToggle = toggleEl && toggleEl.contains(evt.target);
            const clickedInsideTarget = targetEl && targetEl.contains(evt.target);

            // If click was outside both the toggle and target, untoggle it
            if (!clickedInsideToggle && !clickedInsideTarget) {
                _untoggleByName(toggleName);
            }
        });
    }

    /**
     * Main click handler for any element with the data-toggle attribute.
     * @param {Event} evt
     */
    function _handleClick(evt) {
        // Use currentTarget because the listener is added to the toggle element itself
        const toggle = evt.currentTarget;
        const toggleName = toggle.dataset.toggle;

        if (!toggleName) return;

        // --- Case 1: All Toggle ---
        if (toggleName.includes('_all')) {
            _handleToggleAll(toggle, toggleName);
        }
        // --- Case 2: Single Toggle ---
        else {
            const target = document.querySelector(`[data-target="${toggleName}"]`);
            const isToggleOnce = toggle.dataset.toggleOnce === 'true' || toggle.hasAttribute('data-toggle-once');

            if (toggle.classList.contains(ACTIVE_CLASS) && isToggleOnce) {
                return;
            }

            if (target) {
                _collapseOthers(toggleName);
                _handleVisibility(toggle, target);
            }
        }
    }

    /**
     * Finds all toggles and attaches the click listener.
     */
    function _init() {
        const toggles = document.querySelectorAll(TOGGLE_SELECTOR);

        if (toggles.length === 0) {
            console.log('ToggleModule: No elements found with data-toggle attribute.');
            return;
        }

        toggles.forEach(toggle => {
            // Attach the click handler, ensuring 'this' context is the element
            toggle.addEventListener('click', _handleClick);
        });

        // Global event listener for click-outside defocus behavior
        document.addEventListener('click', _handleOutsideClick);
        
        console.log(`ToggleModule: Initialized and observing ${toggles.length} UI toggles.`);
    }

    // --- Public Interface ---
    return {
        init: _init
    };
})();

(() => {
    "use strict";

    ToggleModule.init()

    ScrollObserverModule.registerTrigger('animateNumber', element => {
        animateNumber(element, 6000)
    })
    ScrollObserverModule.registerTrigger('togglePlayState', togglePlayState, false)
    ScrollObserverModule.registerTrigger('flipRight', flipRight, false)
    ScrollObserverModule.init()

    const elementIntervalMap = new Map()

    if (document.querySelector('[data-background-image]')) {
        const backgroundImages = document.querySelectorAll('[data-background-image]')

        backgroundImages.forEach(bgImg => bgImg.addEventListener('mouseover', evt => {
            const backgroundImage = evt.target.dataset.backgroundImage
            const backgroundTarget = evt.target.dataset.backgroundTarget
            const backgroundElement = document.querySelector(`[data-background="${backgroundTarget}"]`)

            if (backgroundElement) {
                backgroundElement.src = backgroundImage
            }
        }))
    }

    if (document.querySelector('[data-listing="video"]')) {
        document.querySelectorAll('[data-listing="video"]').forEach(listing => {
            listing.addEventListener('mouseenter', evt => {
                evt.stopPropagation()
                evt.target.querySelector('video').play()
            })

            listing.addEventListener('mouseleave', evt => {
                evt.stopPropagation()
                evt.target.querySelector('video').pause()
            })
        })
    }

    if (document.querySelector('[data-aos]')) {
        AOS.init({
            startEvent: 'DOMContentLoaded',
            offset: 100,
            duration: 600,
            easing: 'ease-in-out',
            once: true,
            disableOnMobile: true,
        })
    }

    if (document.getElementById('portals')) {
        new Splide('#portals', {
            arrows: true,
            pagination: false,
            perPage: 3,
            perMove: 1,
            gap: '2rem',
            breakpoints: {
                1030: {
                    perPage: 2,
                    gap: '1.5rem',
                },
                580: {
                    perPage: 1,
                }
            }
        })
        .mount()
    }

    if (document.querySelector('[data-charity]')) {
        document.querySelectorAll('[data-charity]').forEach(charity => {
            new Splide(charity, {
                arrows: true,
                pagination: false,
                perPage: 1,
                perMove: 1,
                gap: 0
            })
            .mount()
        })
    }

    if (document.querySelector('[data-sharer]')) {
        const shareButton = document.querySelector('[data-sharer]')
        const shareTitle = document.querySelector('h1').innerText
        const shareText = document.querySelector('p:first-of-type').innerText
        const shareUrl = window.location.url

        if (navigator.share) {
            shareButton.addEventListener('click', async (evt) => {
                evt.preventDefault()

                const postType = document.querySelector('body')?.dataset.layout

                let title = `${shareTitle}. Read full story at nortecfire.com`
                let text = `Check this out. ${shareText}`

                if (postType) {
                    switch (postType) {
                        case 'article':
                            title = `New Article: ${shareTitle}. Read full story at nortecfire.com`
                            text = `Check out this story. ${shareText}`
                            break

                        case 'case-study':
                            title = `New Case Study: ${shareTitle}. Read full case study at nortecfire.com`
                            text = `Check out this case study. ${shareText}`
                            break
                            
                        default:
                            title = `${shareTitle}. Read full story at nortecfire.com`
                            text = `Check this out. ${shareText}`
                    }
                }

                try {
                    await navigator.share({
                        title: title,
                        text: text,
                        url: shareUrl
                    })
                }
                catch (err) {
                    console.log('Error sharing: ', err)
                }
            })
        }
        else {
            shareButton.style.display = 'none'
        }
    }

    if (document.querySelector('[data-custom-selection]')) {
        const customSelection = document.querySelector('[data-custom-selection]')
        const customOptions = document.querySelectorAll('[data-custom-option]')
        const customSelectionList = new Set()

        customOptions.forEach(option => {
            option.addEventListener('click', evt => {
                const value = evt.currentTarget.dataset.customOption.trim()

                if (customSelectionList.has(value)) {
                    customSelectionList.delete(value)
                    evt.currentTarget.classList.remove('is-selected')
                }
                else {
                    customSelectionList.add(value)
                    evt.currentTarget.classList.add('is-selected')
                }

                customSelection.innerHTML = ''
                customSelection.innerHTML = buildCustomSelection(customSelectionList)
            })
        })

        function buildCustomSelection(list) {
            // 1. Convert the Set to an Array to use array methods
            const items = Array.from(list);
            const count = items.length;

            // 2. Handle empty state
            if (count === 0) {
                return `<span class="placeholder">Select services...</span>`; 
            }

            // 3. Handle 1 or 2 items (show them all normally)
            // if (count <= 1) {
            //     return items.join(', ');
            // }

            // 4. Handle more than 2 items ("Service, Service, and X more")
            // const firstOne = items.slice(0, 1).join(', ');
            // const remainingCount = count - 1;
            
            // return `${firstOne}, and ${remainingCount} more`;
            return `${count} service${count > 1 ? 's' : ''} selected`;
        }
    }

    /**
     * Animate the number from dataset.animateFrom to dataset.animateTo
     * @param {HTMLElement} element 
     * @param {number} duration Animation duration in milliseconds (default: 2000ms)
     */
    function animateNumber(element, duration = 2000) {
        const startNumber = Number(element.dataset.animateFrom) || 0;
        const endNumber = Number(element.dataset.animateTo) || 0;
        
        let startTime = null;

        function step(timestamp) {
            if (!startTime) startTime = timestamp;
            
            // Calculate progress as a ratio between 0 and 1
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Optional: Ease-out curve (makes the animation slow down near the end)
            // Remove the line below if you prefer a strict linear increment.
            const easedProgress = 1 - Math.pow(1 - progress, 3);

            // Calculate current value based on progress
            const currentNumber = Math.floor(startNumber + (endNumber - startNumber) * easedProgress);

            // Update display
            element.textContent = currentNumber.toLocaleString();

            // Continue animation until duration is reached
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                element.textContent = endNumber.toLocaleString(); // Ensure exact target on completion
            }
        }

        requestAnimationFrame(step);
    }

    // /**
    //  * Animate the increment of numbers from 0 to max value
    //  * @param {Object} element 
    //  */
    // function animateNumber(element) {

    //     const startNumber = Number(element.dataset.animateFrom);
    //     const endNumber = Number(element.dataset.animateTo);

    //     let currentNumber = startNumber;
    //     let increment = startNumber < endNumber ? 1 : -1; // Determine whether to increment or decrement
    //     const intervalTime = 50; // Time interval in milliseconds
    
    //     // Interval function to update the number
    //     const interval = setInterval(function() {
    //         // Update the current number by the current increment
    //         currentNumber += increment;
    
    //         // Dynamically change the increment based on the current number
    //         if (currentNumber > 10 && currentNumber < 100) {
    //             increment = startNumber < endNumber ? 10 : -10; // Switch to increment/decrement of 10
    //         } else if (currentNumber > 100 && currentNumber < 1000) {
    //             increment = startNumber < endNumber ? 100 : -100; // Switch to increment/decrement of 100
    //         } else if (currentNumber > 1000 && currentNumber < 5000) {
    //             increment = startNumber < endNumber ? 500 : -500 // Switch to increment/decrement of 1000
    //         } else if (currentNumber > 5000 && currentNumber < 10000) {
    //             increment = startNumber < endNumber ? 1500 : -1500 // Switch to increment/decrement of 10000
    //         } else if (currentNumber > 10000) {
    //             increment = startNumber < endNumber ? 5000 : -5000
    //         }
    
    //         // Stop the animation if it reaches the end number
    //         if ((startNumber < endNumber && currentNumber >= endNumber) || (startNumber > endNumber && currentNumber <= endNumber)) {
    //             clearInterval(interval);
    //             currentNumber = endNumber; // Ensure it ends exactly at the target number
    //         }
    
    //         // Update the display
    //         element.textContent = currentNumber;
    //     }, intervalTime);
    // }

    /**
     * Toggle play state of the page video
     */
    function togglePlayState(element, isIntersecting) {
        if (isIntersecting) {
            element.play()
        }
        else {
            element.pause()
        }
    }

    function flipRight(element, isIntersecting) {
        const className = 'flip-right';
        const intervalTime = 3000; // 0.3 seconds

        // 1. Element is INTERSECTING (entering viewport)
        if (isIntersecting) {
            // Add the class immediately
            element.classList.add(className);

            // Check if an interval is ALREADY running for this specific element
            if (!elementIntervalMap.has(element)) {
                // Start the interval
                const intervalId = setInterval(() => {
                    const probability = 0.5;
                    const randomNumber = Math.random();

                    // Randomly toggle the class
                    if (randomNumber < probability) {
                        element.classList.remove(className);
                    } else {
                        element.classList.add(className);
                    }
                }, intervalTime);

                // Store the ID in the Map, associated with this element
                elementIntervalMap.set(element, intervalId);
            }
        }
        // 2. Element is NOT INTERSECTING (leaving viewport)
        else {
            // Check if an interval is running for this specific element
            if (elementIntervalMap.has(element)) {
                // Stop and clear the interval
                clearInterval(elementIntervalMap.get(element));
                // Remove the entry from the Map
                elementIntervalMap.delete(element);
            }

            // Ensure the animation class is removed when leaving the viewport
            element.classList.remove(className);
        }
    }

    // Web forms
    if (document.querySelector('[name="jsToken"]') && document.querySelector('[name="formTime"]')) {
        document.querySelector('[name="jsToken"').value = Math.random().toString(36).substring(2, 10)
        document.querySelector('[name="formTime"]').value = Date.now()
    }

    if (document.querySelector('[name="website"]')) {
        document.querySelector('[name="website"]').parentElement.parentElement.style = 'position: absolute; top: -1000em; left: -1000em;'
        document.querySelector('[name="website"]').removeAttribute('required')
        document.querySelector('[name="website"]').value = '';
    }

    if (document.getElementById('quote')) {
        const fields = document.querySelectorAll('input')

        fields.forEach(field => field.addEventListener('input', evt => {
            if (evt.target.parentElement.querySelector('.form__error')) {
                evt.target.parentElement.querySelector('.form__error').style.display = 'none'
            }
        }))
    }

    if (document.getElementById('blog-carousel')) {
        new Splide('#blog-carousel', {
            perPage: 3,
            perMove: 1,
            pagination: false,
            autoplay: false,
            gap: 'clamp(1rem, 3.5vw, 2rem)',
            breakpoints: {
                1024: {
                    perPage: 2,
                }
            }
        }).mount()
    }
})()